import React, { useContext, useCallback } from 'react';
import {
  IColumn,
  ILopandeVariables,
  IRadbaseradVariables,
} from './EkonomiskAnalysTableBase';
import {
  ISelectedAnalysCodePrivilege,
  ISelectedAnalys,
} from '../../EkonomiskAnalysPage';
import {
  IFilledLocalAnalys,
  ILocalAnalys,
} from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import EkonomiskAnalysLopandeTable from '../EkonomiskAnalysLopandeTable/EkonomiskAnalysLopandeTable';
import EkonomiskAnalysRadBaseradTable from '../EkonomiskAnalysRadBaseradTable';
import { EkonomiskAnalysContext } from '../Context/EkonomiskAnalysContext';
import EDSortableTable, {
  ISortOrder,
} from '../../../../../../../BaseComponents/Table/Components/EDSortableTable';
import { IRow } from '../../Queries/EkonomiskAnalysQuery/EkonomiskAnalysQuery';
import { numberOrStringCompare } from '../../../../../../../BaseComponents/Table/Components/SortableTableUtils';

interface IProps {
  selectedAnalys: ISelectedAnalys;
  columns: IColumn[];
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  showComparsion: boolean;
  updateImpersonatedCodePrivilege: (
    codePrivilege?: ISelectedAnalysCodePrivilege
  ) => void;
  settingsForCurrentlyShown: IFilledLocalAnalys;
  updatedSettings: ILocalAnalys;
  onDenominationChange: (denomination: number) => void;
  lopandeVariables?: ILopandeVariables;
  radbaseradVariables?: IRadbaseradVariables;
  page?: number;
  rowsPerPage?: number;
  sammanfattningsMode?: boolean;
}

const lopandeTableCellCompare = (
  a: string | number,
  b: string | number,
  order: ISortOrder
): number => {
  return numberOrStringCompare(a.toString(), b.toString(), order);
};

/**
 * Komponent som visar an anlystabell av typ löpande eller radbaserad
 */
const EkonomiskAnalysTable: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { userInfo } = useContext(EkonomiskAnalysContext);
  const lopandeVariables = props.lopandeVariables;
  const radbaseradVariables = props.radbaseradVariables;

  const getLopandeTableCellValue = useCallback(
    (row: IRow, columnName: string) => {
      const index = parseInt(columnName, 10);
      const col = props.columns[index];
      const colValue = parseFloat(row.data[index].value);
      if (col && !isNaN(colValue)) {
        if (col.inverted) {
          return -parseFloat(row.data[index].value);
        } else {
          return parseFloat(row.data[index].value);
        }
      }

      return 0;
    },
    [props.columns]
  );

  return (
    <>
      {lopandeVariables && (
        <EDSortableTable
          sortInfo={{
            rows: lopandeVariables.rows,
            properties: props.settingsForCurrentlyShown.sums
              .slice(0, 3)
              .map((_, i) => ({
                caption: '',
                order: 'asc',
                orderBy: i.toString(),
              })),
          }}
          getValue={getLopandeTableCellValue}
          compareFunction={lopandeTableCellCompare}
        >
          {({ sortInfo, createSortHandler }) => {
            return (
              <EkonomiskAnalysLopandeTable
                columns={props.columns}
                analysSettings={props.settingsForCurrentlyShown}
                updatedAnalysSettingsUrval={props.updatedSettings.urval}
                showComparisonNumber={props.showComparsion}
                rows={lopandeVariables.rows}
                impersonatedCodePrivilege={props.impersonatedCodePrivilege}
                updateImpersonatedCodePrivilege={
                  props.updateImpersonatedCodePrivilege
                }
                totalSumRow={lopandeVariables.totalSumRow}
                onDenominationChange={props.onDenominationChange}
                expandedLevel={lopandeVariables.expandedSumIndex}
                selectedAnalys={props.selectedAnalys}
                page={props.page}
                rowsPerPage={props.rowsPerPage}
                updatedAnalysSettings={props.updatedSettings}
                utanSummeringSumColCount={
                  lopandeVariables.utanSummeringSumColCount
                }
                sortInfo={sortInfo}
                createSortHandler={createSortHandler}
                sammanfattningsMode={props.sammanfattningsMode}
              />
            );
          }}
        </EDSortableTable>
      )}
      {radbaseradVariables && (
        <EkonomiskAnalysRadBaseradTable
          analys={{
            columns: props.columns,
            rows: radbaseradVariables.rows,
            isEmpty: radbaseradVariables.isEmpty,
          }}
          selectedAnalys={props.selectedAnalys}
          impersonatedCodePrivilege={props.impersonatedCodePrivilege}
          showComparisonNumber={props.showComparsion}
          analysSettings={props.settingsForCurrentlyShown}
          updatedAnalysSettings={props.updatedSettings}
          updateImpersonatedCodePrivilege={
            props.updateImpersonatedCodePrivilege
          }
          onDenominationChange={props.onDenominationChange}
          handleScroll={radbaseradVariables.handleScroll}
          inititialLopandeUtanSummeringRowsPerPage={
            userInfo?.utanSummeringRowsPerPage
          }
        />
      )}
    </>
  );
};

export default EkonomiskAnalysTable;
